import { useMemo } from 'react';

import { useMyStore, useQuery } from '../hooks';

import {
  GET_PRODUCT_DOES_FIT_DATA,
  GET_STAGGERED_PRODUCT_DOES_FIT_DATA
} from '../../graphql/check-fit-queries';
import {
  GET_IS_OTHER_VEHICLE_OE_BY_PRODUCT_CODE,
  GET_IS_OTHER_VEHICLE_OE_BY_PRODUCT_SET_BY_CODE
} from '../../graphql/product-detail-queries';

import { getVehicleInput } from '../../utils/fitment/fitment';

import { DONT_FIT, FIT } from '../../constants/fit';

export default function useDoesProductFit({
  fitment,
  isCanonical,
  isStaggered,
  productCodes,
  skipIsOtherVehicleOE = true
} = {}) {
  const { myStore } = useMyStore();
  const { data, isLoading } = useQuery(
    isStaggered
      ? GET_STAGGERED_PRODUCT_DOES_FIT_DATA
      : GET_PRODUCT_DOES_FIT_DATA,
    {
      fetchPolicy: 'no-cache',
      skip:
        !fitment ||
        !(Array.isArray(productCodes) && productCodes.filter(Boolean).length) ||
        !myStore?.code,
      variables: {
        productCodes,
        storeCode: myStore?.code,
        vehicleInfo: getVehicleInput(fitment)
      }
    }
  );

  const { data: oeData } = useQuery(
    isStaggered && productCodes?.[0]?.front && productCodes?.[0]?.rear
      ? GET_IS_OTHER_VEHICLE_OE_BY_PRODUCT_SET_BY_CODE
      : GET_IS_OTHER_VEHICLE_OE_BY_PRODUCT_CODE,
    {
      skip:
        skipIsOtherVehicleOE ||
        !fitment ||
        !productCodes?.[0] ||
        !myStore?.code,
      variables: {
        ...(isStaggered && productCodes?.[0]?.front && productCodes?.[0]?.rear
          ? {
              frontCode: productCodes?.[0]?.front,
              rearCode: productCodes?.[0]?.rear
            }
          : { productCode: productCodes?.[0] }),
        storeCode: myStore?.code,
        treadwellRequest: null,
        vehicleInfo: getVehicleInput(fitment)
      }
    }
  );

  const { canonicalDoesFitEnum, doesFitEnumMap } = useMemo(() => {
    const checkFitProducts = isStaggered
      ? data?.checkFit?.productSets
      : data?.checkFit?.product;
    return (
      checkFitProducts?.reduce(
        (acc, result) => {
          if (result.doesFit === DONT_FIT) {
            acc.canonicalDoesFitEnum = DONT_FIT;
          } else if (
            result.doesFit === FIT &&
            acc.canonicalDoesFitEnum !== DONT_FIT
          ) {
            acc.canonicalDoesFitEnum = FIT;
          }

          acc.doesFitEnumMap[result.productCode] = result.doesFit;
          return acc;
        },
        { canonicalDoesFitEnum: null, doesFitEnumMap: {} }
      ) || {}
    );
  }, [data, isStaggered]);

  return {
    canonicalDoesFitEnum: isCanonical ? canonicalDoesFitEnum : null,
    doesFitEnumMap,
    isLoading,
    isOtherVehiclesOE:
      isStaggered && productCodes?.[0]?.front && productCodes?.[0]?.rear
        ? oeData?.product?.setByCode?.isOtherVehiclesOE
        : oeData?.product?.byCode?.isOtherVehiclesOE
  };
}
